import { useCallback, useEffect, useState } from 'react';

import { getCatalogueVisibility } from 'services/api/catalogue';

import { AUTH_SESSION_EVENT } from './useOrgBranding';

// Cached for the same reason tier entitlement is: without it the menu would
// flicker its switched-off items back in on every reload while the request flies.
const CACHE_KEY = 'ayro-catalogue-visibility';

/** Dispatched by Features and Pages after a feature/page toggle. */
export const CATALOGUE_VISIBILITY_EVENT = 'ayro-catalogue-visibility-changed';

const read = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    if (value) localStorage.setItem(CACHE_KEY, JSON.stringify(value));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* storage unavailable — visibility simply is not cached */
  }
};

/**
 * Which catalogue features and pages are switched off in Features and Pages.
 *
 * Returns null until known, and callers MUST read null as "not yet known" and
 * hide nothing — same rule as useMyFeatures. Blanking navigation on a pending
 * request or a network blip is far worse than briefly showing a switched-off item,
 * and the server refuses the request anyway, so nothing is actually reachable.
 *
 * This is the switch; useMyFeatures is the entitlement. Both must pass.
 */
export default function useCatalogueVisibility() {
  const [data, setData] = useState(() => read());

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('serviceToken')) {
      setData(null);
      write(null);
      return;
    }
    try {
      const res = await getCatalogueVisibility();
      setData(res);
      write(res);
    } catch {
      // Leave the previous value in place; see the note above on failing open.
    }
  }, []);

  useEffect(() => {
    refresh();
    const onSession = () => refresh();
    window.addEventListener(AUTH_SESSION_EVENT, onSession);
    window.addEventListener('storage', onSession);
    // Features and Pages broadcasts this after a toggle so the sidebar updates
    // without a reload — otherwise an admin switches something off and has to
    // refresh before believing it worked.
    window.addEventListener(CATALOGUE_VISIBILITY_EVENT, onSession);
    return () => {
      window.removeEventListener(AUTH_SESSION_EVENT, onSession);
      window.removeEventListener('storage', onSession);
      window.removeEventListener(CATALOGUE_VISIBILITY_EVENT, onSession);
    };
  }, [refresh]);

  return data;
}
